import React from "react";
//#region types
import { UtilFormTypes } from "models/formTypes";
import { CostForwardingPut } from "models/utilTypes/costForwardingTypes";
import {
  applyCostForwardingValues,
  getCostForwardingFormValues,
  getCostForwardingValuesAfterSave,
} from "./costForwardingMarkingModel";
//#endregion
//#region components
import { useCostForwardingMarkingContext } from "./costForwardingMarkingContext";
import { getCostForwardingSavedEventName } from "./useSaveCostForwardingMarking";
//#endregion
import { EventHelper } from "utils/helpers";

type SetValues<T> = (values: React.SetStateAction<T>, shouldValidate?: boolean) => unknown;

/**
 * Keeps a to-do form's cost-forwarding fields in step with the marking by patching just those fields.
 * Going through the form's default values instead would reinitialise the whole form and throw away
 * whatever else the user has filled in.
 */
function useCostForwardingFormSync<T extends UtilFormTypes.CostForwardingMarking>(setValues: SetValues<T>) {
  const { marking, isAvailable, source, sourceId, supplierName } = useCostForwardingMarkingContext();

  React.useEffect(() => {
    setValues((values) =>
      applyCostForwardingValues(values, getCostForwardingFormValues(marking, isAvailable, supplierName))
    );
  }, [marking, isAvailable]);

  React.useEffect(() => {
    if (!source || !sourceId) return;

    const eventName = getCostForwardingSavedEventName(source, sourceId);
    const onSaved = (event: CustomEvent<CostForwardingPut>) =>
      setValues((values) => applyCostForwardingValues(values, getCostForwardingValuesAfterSave(event.detail)));

    EventHelper.subscribeEvent(eventName, onSaved);
    return () => EventHelper.unsubscribeEvent(eventName, onSaved);
  }, [source, sourceId]);
}

export default useCostForwardingFormSync;
